import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Badge,
  Button,
  Pagination,
  Form,
  Dropdown,
} from "react-bootstrap";
import Tooltip from "@mui/material/Tooltip";
import { LEAD_STATUS_BG } from "../../../constants";
import { deleteLeads, transferLeads } from "../../../api/lead";
import CustomPagination from "../../../components/CustomPagination";

const LOCAL_STORAGE_KEYS = {
  currentPage: "leadTable_currentPage",
  priorityFilter: "leadTable_priorityFilter",
  statusFilter: "leadTable_statusFilter",
  sourceFilter: "leadTable_sourceFilter",
  followupFilter: "leadTable_followupFilter",
  assignedToFilter: "leadTable_assignedToFilter",
  classFilter: "leadTable_classFilter",
  dateSortOrder: "leadTable_dateSortOrder",
  schoolFilter: "leadTable_schoolFilter",
  leadsPerPage: "leadTable_leadsPerPage",
};

const saveToLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getFromLocalStorage = (key, defaultValue) => {
  try {
    const value = localStorage.getItem(key);

    if (!value) return defaultValue;
    const parsed = JSON.parse(value);
    if (typeof parsed !== typeof defaultValue) {
      console.warn(
        `Type mismatch for key "${key}". Expected ${typeof defaultValue} but got ${typeof parsed}`
      );
      return defaultValue;
    }

    return parsed;
  } catch (e) {
    return defaultValue;
  }
};

const LeadTable = (props) => {
  const { leads, users, configuration } = props;
  const navigate = useNavigate();

  const userType = localStorage.getItem("type");

  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(users[0]?.id || null);
  const [leadsPerPage, setLeadsPerPage] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.leadsPerPage, 20)
  );
  const [currentPage, setCurrentPage] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.currentPage, 1)
  );

  const safeLeads = Array.isArray(leads) ? leads : [];
  const safeConfig = configuration || {};
  const safeLeadStatuses = Array.isArray(safeConfig.lead_status)
    ? safeConfig.lead_status
    : [];
  const safePriorities = Array.isArray(safeConfig.priority)
    ? safeConfig.priority
    : [];
  const safeLeadSources = Array.isArray(safeConfig.lead_sources)
    ? safeConfig.lead_sources
    : [];
  const safeFollowUpType = Array.isArray(safeConfig.followup_type)
    ? safeConfig.followup_type
    : [];

  const assignedUsers = [
    ...new Set(
      safeLeads.flatMap((lead) =>
        Array.isArray(lead.assigned_data)
          ? lead.assigned_data.map((user) => user.name).filter(Boolean)
          : []
      )
    ),
  ];

  const lead_priority_list = useMemo(() => {
    const prioritySet = new Set();
    let hasNoPriority = false;

    safeLeads.forEach((lead) => {
      if (lead.priority === "" || lead.priority == null) {
        hasNoPriority = true;
      } else {
        prioritySet.add(Number(lead.priority));
      }
    });

    const priorities = safePriorities.filter((p) => prioritySet.has(p.id));

    if (hasNoPriority) {
      priorities.unshift({
        id: "", // IMPORTANT: empty string, not null
        name: "No Priority",
      });
    }

    return priorities;
  }, [safeLeads, safePriorities]);

  const lead_source_list = useMemo(() => {
    const sourceSet = new Set();
    let hasNoSource = false;

    safeLeads.forEach((lead) => {
      if (lead.source === "" || lead.source == null) {
        hasNoSource = true;
      } else {
        sourceSet.add(Number(lead.source));
      }
    });

    const sources = safeConfig.lead_sources.filter((src) =>
      sourceSet.has(src.id)
    );

    if (hasNoSource) {
      sources.unshift({
        id: "",
        name: "No Source",
      });
    }

    return sources;
  }, [safeLeads, safeConfig.lead_sources]);

  const lead_status_list = useMemo(() => {
    const statusSet = new Set();
    let hasNoStatus = false;

    safeLeads.forEach((lead) => {
      if (lead.status_id === "" || lead.status_id == null || lead.status_id == 0) {
        hasNoStatus = true;
      } else {
        statusSet.add(Number(lead.status_id));
      }
    });

    const statuses = safeConfig.lead_status.filter((status) =>
      statusSet.has(status.id)
    );

    if (hasNoStatus) {
      statuses.unshift({
        id: "",
        name: "No Status",
      });
    }

    return statuses;
  }, [safeLeads, safeConfig.lead_status]);

  const lead_followup_list = useMemo(() => {
    const followupSet = new Set();
    let hasNoFollowup = false;

    safeLeads.forEach((lead) => {
      if (lead.followUpType === "" || lead.followUpType == null) {
        hasNoFollowup = true;
      } else {
        followupSet.add(Number(lead.followUpType));
      }
    });

    const followups = safeConfig.followup_type.filter((f) =>
      followupSet.has(f.id)
    );

    if (hasNoFollowup) {
      followups.unshift({
        id: "",
        name: "No Follow-Up",
      });
    }

    return followups;
  }, [safeLeads, safeConfig.followup_type]);

  const studentClass = useMemo(() => {
    const classSet = new Set();
    let hasNoClass = false;

    safeLeads.forEach((lead) => {
      if (!lead.studentClass || lead.studentClass === "") {
        hasNoClass = true;
      } else {
        classSet.add(lead.studentClass);
      }
    });

    const classes = Array.from(classSet).map((name) => ({
      id: name,
      name,
    }));

    if (hasNoClass) {
      classes.unshift({
        id: "",
        name: "No Class",
      });
    }

    return classes;
  }, [safeLeads]);

  const school = useMemo(() => {
    const schoolSet = new Set();
    let hasNoSchool = false;

    safeLeads.forEach((lead) => {
      if (!lead.school || lead.school === "") {
        hasNoSchool = true;
      } else {
        schoolSet.add(lead.school);
      }
    });

    const schools = Array.from(schoolSet).map((name) => ({
      id: name,
      name,
    }));

    if (hasNoSchool) {
      schools.unshift({
        id: "",
        name: "No School",
      });
    }

    return schools;
  }, [safeLeads]);

  const hasUnassigned = safeLeads.some(
    (lead) =>
      !Array.isArray(lead.assigned_data) || lead.assigned_data.length === 0
  );

  if (hasUnassigned) {
    assignedUsers.push("Unassigned");
  }

  // Filter states
  const [priorityFilter, setPriorityFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.priorityFilter, [])
  );
  const [statusFilter, setStatusFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.statusFilter, [])
  );
  const [sourceFilter, setSourceFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.sourceFilter, [])
  );
  const [followupFilter, setFollowupFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.followupFilter, [])
  );
  const [assignedToFilter, setAssignedToFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.assignedToFilter, [])
  );
  const [classFilter, setClassFilter] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.classFilter, [])
  );
  const [schoolFilter, setSchoolFilter] = useState(() => {
    const val = getFromLocalStorage(LOCAL_STORAGE_KEYS.schoolFilter, []);
    return Array.isArray(val) ? val : [];
  });
  const [dateSortOrder, setDateSortOrder] = useState(() =>
    getFromLocalStorage(LOCAL_STORAGE_KEYS.dateSortOrder, "desc")
  );

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.priorityFilter, priorityFilter);
  }, [priorityFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.statusFilter, statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.sourceFilter, sourceFilter);
  }, [sourceFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.followupFilter, followupFilter);
  }, [followupFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.assignedToFilter, assignedToFilter);
  }, [assignedToFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.classFilter, classFilter);
  }, [classFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.schoolFilter, schoolFilter);
  }, [schoolFilter]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.dateSortOrder, dateSortOrder);
  }, [dateSortOrder]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.currentPage, currentPage);
  }, [currentPage]);

  useEffect(() => {
    saveToLocalStorage(LOCAL_STORAGE_KEYS.leadsPerPage, leadsPerPage);
  }, [leadsPerPage]);

  // Filter handlers
  const handlePriorityFilterChange = (id) => {
    setPriorityFilter((prev) =>
      prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    );
  };

  const handleStatusFilterChange = (id) => {
    setStatusFilter((prev) =>
      prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    );
  };

  const handleSourceFilterChange = (id) => {
    setSourceFilter((prev) =>
      prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    );
  };

  const handleFollowUpFilterChange = (id) => {
    setFollowupFilter((prev) =>
      prev.includes(id) ? prev.filter((val) => val !== id) : [...prev, id]
    );
  };

  const handleAssignedToFilterChange = (userName) => {
    setAssignedToFilter((prev) =>
      prev.includes(userName)
        ? prev.filter((name) => name !== userName)
        : [...prev, userName]
    );
  };

  const handleClassFilterChange = (id) => {
    setClassFilter((prevFilter) =>
      prevFilter.includes(id)
        ? prevFilter.filter((item) => item !== id)
        : [...prevFilter, id]
    );
  };

  const handleSchoolFilterChange = (id) => {
    setSchoolFilter((prevFilter) =>
      prevFilter.includes(id)
        ? prevFilter.filter((item) => item !== id)
        : [...prevFilter, id]
    );
  };

  // Filter logic
  const filteredLeads = useMemo(() => {
    let leads = safeLeads.filter((lead) => {
      const isNoPriority = lead.priority === "" || lead.priority == null;

      const isNoSource = lead.source === "" || lead.source == null;

      const isNoStatus = lead.status_id === "" || lead.status_id == null || lead.status_id == 0;

      const isNoFollowup =
        lead.followUpType === "" || lead.followUpType == null;

      const isNoClass = lead.studentClass === "" || lead.studentClass == null;

      const isNoSchool = lead.school === "" || lead.school == null;

      const priorityMatch =
        priorityFilter.length === 0 ||
        (priorityFilter.includes("") && isNoPriority) ||
        (!isNoPriority && priorityFilter.includes(Number(lead.priority)));

      const sourceMatch =
        sourceFilter.length === 0 ||
        (sourceFilter.includes("") && isNoSource) ||
        (!isNoSource && sourceFilter.includes(Number(lead.source)));

      const statusMatch =
        statusFilter.length === 0 ||
        (statusFilter.includes("") && isNoStatus) ||
        (!isNoStatus && statusFilter.includes(Number(lead.status_id)));

      const followupMatch =
        followupFilter.length === 0 ||
        (followupFilter.includes("") && isNoFollowup) ||
        (!isNoFollowup && followupFilter.includes(Number(lead.followUpType)));

      const classMatch =
        classFilter.length === 0 ||
        (classFilter.includes("") && isNoClass) ||
        (!isNoClass && classFilter.includes(lead.studentClass));

      const schoolMatch =
        schoolFilter.length === 0 ||
        (schoolFilter.includes("") && isNoSchool) ||
        (!isNoSchool && schoolFilter.includes(lead.school));

      const assignedNames = Array.isArray(lead.assigned_data)
        ? lead.assigned_data.map((u) => u.name).filter(Boolean)
        : [];

      const isUnassigned =
        !Array.isArray(lead.assigned_data) || lead.assigned_data.length === 0;

      const assignedToMatch =
        assignedToFilter.length === 0 ||
        (assignedToFilter.includes("Unassigned") && isUnassigned) ||
        assignedNames.some((name) => assignedToFilter.includes(name));

      return (
        priorityMatch &&
        statusMatch &&
        sourceMatch &&
        classMatch &&
        assignedToMatch &&
        followupMatch &&
        schoolMatch
      );
    });

    leads = [...leads];

    if (dateSortOrder) {
      leads.sort((a, b) => {
        const rawA = a?.updated_at?.$date ?? a?.updated_at;
        const rawB = b?.updated_at?.$date ?? b?.updated_at;

        const dateA = rawA ? new Date(rawA).getTime() : 0;
        const dateB = rawB ? new Date(rawB).getTime() : 0;

        return dateSortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });
    }

    return leads;
  }, [
    safeLeads,
    priorityFilter,
    statusFilter,
    sourceFilter,
    followupFilter,
    classFilter,
    assignedToFilter,
    schoolFilter,
    dateSortOrder,
  ]);

  // Pagination
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredLeads.length / leadsPerPage)
  );
  const start = filteredLeads.length > 0 ? indexOfFirstLead + 1 : 0;
  const end = Math.min(indexOfLastLead, filteredLeads.length);

  const statusIds = safeLeadStatuses.map((status) => status.id);
  const count = statusIds.reduce((acc, id) => {
    acc[id] = 0;
    return acc;
  }, {});

  safeLeads.forEach((lead) => {
    if (count.hasOwnProperty(lead.status_id)) {
      count[parseInt(lead.status_id)]++;
    }
  });

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
  };

  const handleCheckboxChange = (e, leadId) => {
    if (e.target.checked) {
      setSelectedLeads((prev) => [...prev, leadId]);
    } else {
      setSelectedLeads((prev) => prev.filter((id) => id !== leadId));
    }
  };

  const handleEdit = (lead) => {
    const data = {
      lead_data: lead,
      source: "lead-list",
    };
    navigate(`/home/lead-edit/L-${`*******${lead?.lead_id.slice(-10)}`}`, {
      state: data,
    });
  };

  const handleTransferLeads = async () => {
    const data = {
      leads: selectedLeads,
      assign_to: selectedUserId,
    };
    try {
      const response = await transferLeads(data);
      if (response.status === true) {
        alert("Leads Transferred successfully!");
        window.location.reload();
      } else {
        alert("Import failed. Server returned an error: " + response.message);
      }
    } catch (error) {
      alert("There was an error in transferring leads");
    }
  };

  const handleDateSort = (order) => {
    setDateSortOrder(order);
  };

  const deleteLead = async () => {
    const data = {
      leads: selectedLeads,
    };
    try {
      const response = await deleteLeads(data);
      if (response.status === true) {
        alert("Leads Deleted successfully!");
        window.location.reload();
      } else {
        alert("Import failed. Server returned an error: " + response.message);
      }
    } catch (error) {
      alert("There was an error in Deleting leads");
    }
  };

  const handleResetFilters = () => {
    setPriorityFilter([]);
    setStatusFilter([]);
    setSourceFilter([]);
    setFollowupFilter([]);
    setAssignedToFilter([]);
    setClassFilter([]);
    setSchoolFilter([]);
    setDateSortOrder(null);
    setCurrentPage(1);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.leadsPerPage);
    setLeadsPerPage(20);

    Object.values(LOCAL_STORAGE_KEYS).forEach((key) =>
      localStorage.removeItem(key)
    );
  };

  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => handlePageChange(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <>
      <h2 className="mb-2">
        Lead Stats
        <Button
          variant="outline-secondary"
          className="mx-2 "
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </h2>
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="row g-3">
            {safeLeadStatuses.map((status) =>
              count[status.id] !== 0 ? (
                <div className="col" key={status.id}>
                  <div className="card p-3">
                    <h6 className="mb-0">{status.name}</h6>
                    <p className="mb-0 fs-5 fw-bold">{count[status.id]}</p>
                  </div>
                </div>
              ) : null
            )}
          </div>
        </div>
      </div>

      {selectedLeads.length > 0 && (
        <div className="mb-3 d-flex align-items-center gap-3">
          <Dropdown>
            <Dropdown.Toggle
              className="btn-transfer-to"
              variant="primary"
              id="dropdownMenuButton"
            >
              Transfer To
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ padding: "10px" }}>
              {users.map((user, index) => (
                <Form.Check
                  key={index}
                  type="radio"
                  name="user-radio-group"
                  id={`user-radio-${index}`}
                  label={user?.name}
                  checked={selectedUserId === user?.id}
                  onChange={() => handleUserSelect(user?.id)}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="primary primary-button"
            onClick={() => handleTransferLeads()}
          >
            Transfer Leads
          </Button>

          <Button
            type="button"
            className="btn btn-danger"
            onClick={() => deleteLead()}
          >
            Delete Lead
          </Button>
        </div>
      )}

      <Table striped bordered hover responsive className="bg-white">
        <thead className="bg-light">
          <tr>
            {(userType === "Admin" || userType === "Manager") && (
              <th>
                <input
                  type="checkbox"
                  checked={
                    currentLeads.length > 0 &&
                    selectedLeads.length === currentLeads.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLeads(
                        currentLeads.map((lead) => lead.lead_id)
                      );
                    } else {
                      setSelectedLeads([]);
                    }
                  }}
                />
              </th>
            )}
            {/* <th>Lead ID</th> */}
            <th>Name</th>
            <th>Contact</th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Grade
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "10px",
                  }}
                >
                  {studentClass.map((c) => (
                    <Form.Check
                      key={c.id}
                      type="checkbox"
                      id={`class-filter-${c.id}`} // Unique ID for accessibility
                      label={c.name}
                      checked={classFilter.includes(c.id)}
                      onChange={() => handleClassFilterChange(c.id)}
                      className="mb-2" // Optional spacing between items
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  School
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    padding: "10px",
                  }}
                >
                  {school.map((c) => (
                    <Form.Check
                      key={c.id}
                      type="checkbox"
                      id={`class-filter-${c.id}`} // Unique ID for accessibility
                      label={c.name}
                      checked={schoolFilter.includes(c.id)}
                      onChange={() => handleSchoolFilterChange(c.id)}
                      className="mb-2" // Optional spacing between items
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Priority
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {lead_priority_list.map((p) => (
                    <Form.Check
                      key={p.id}
                      type="checkbox"
                      label={p.name}
                      checked={priorityFilter.includes(p.id)}
                      onChange={() => handlePriorityFilterChange(p.id)}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Status
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {lead_status_list.map((s) => (
                    <Form.Check
                      key={s.id}
                      type="checkbox"
                      label={s.name}
                      checked={statusFilter.includes(s.id)}
                      onChange={() => handleStatusFilterChange(s.id)}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Source
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {lead_source_list.map((src) => (
                    <Form.Check
                      key={src.id}
                      type="checkbox"
                      label={src.name}
                      checked={sourceFilter.includes(src.id)}
                      onChange={() => handleSourceFilterChange(src.id)}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Follow Up Type
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "200px", overflowY: "auto" }}
                >
                  {lead_followup_list.map((src) => (
                    <Form.Check
                      key={src.id}
                      type="checkbox"
                      label={src.name}
                      checked={followupFilter.includes(src.id)}
                      onChange={() => handleFollowUpFilterChange(src.id)}
                    />
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </th>
            {(userType === "Admin" || userType === "Manager") && (
              <th>
                <Dropdown>
                  <Dropdown.Toggle variant="light" className="w-100 text-start">
                    Assigned To
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {assignedUsers.map((user, index) => (
                      <Form.Check
                        key={index}
                        type="checkbox"
                        label={user}
                        checked={assignedToFilter.includes(user)}
                        onChange={() => handleAssignedToFilterChange(user)}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </th>
            )}
            <th>Latest Comment</th>
            <th>
              <Dropdown>
                <Dropdown.Toggle variant="light" className="w-100 text-start">
                  Last Updated At
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleDateSort("desc")}>
                    Newest to Oldest
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleDateSort("asc")}>
                    Oldest to Newest
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentLeads.length > 0 ? (
            currentLeads.map((lead, index) => (
              <tr key={index}>
                {(userType === "Admin" || userType === "Manager") && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.lead_id)}
                      onChange={(e) => handleCheckboxChange(e, lead.lead_id)}
                    />
                  </td>
                )}
                {/* <td>{lead?.lead_id}</td> */}
                <td>
                  <p className="fw-normal mb-1">{lead?.name.split(" ")[0]}</p>
                  <p className="text-muted mb-0">{lead?.name.split(" ")[1]}</p>
                  {/* <p className="text-muted mb-0">{lead?.email}</p> */}
                </td>
                <td>
                  <p className="fw-normal mb-1">
                    {lead?.phone ? `*******${lead.phone.slice(-3)}` : ""}
                    {/* {lead?.phone ? lead?.phone : 'NA'} */}
                  </p>
                </td>
                <td>{lead?.studentClass}</td>
                <td>{lead?.school}</td>
                <td>
                  <p className="fw-normal mb-1">
                    {lead?.priority
                      ? safePriorities.find(
                          (p) => p.id === Number(lead.priority)
                        )?.name || ""
                      : ""}
                  </p>
                </td>
                <td>
                  {lead?.lead_status?.[0]?.name && (
                    <Badge
                      bg={
                        LEAD_STATUS_BG[parseInt(lead.status_id)] || "secondary"
                      }
                    >
                      {lead.lead_status[0].name}
                    </Badge>
                  )}
                </td>
                <td>
                  {lead?.source
                    ? safeLeadSources.find(
                        (src) => src.id === Number(lead.source)
                      )?.name || ""
                    : ""}
                </td>
                <td>
                  {lead?.followUpType
                    ? safeFollowUpType.find(
                        (src) => src.id === Number(lead.followUpType)
                      )?.name || ""
                    : ""}
                </td>
                {(userType === "Admin" || userType === "Manager") && (
                  <td>
                    {Array.isArray(lead?.assigned_data) &&
                    lead.assigned_data.length > 0 &&
                    lead.assigned_data[0]?.name
                      ? lead.assigned_data[0].name
                      : ""}
                  </td>
                )}
                {/* <td title={lead?.lead_history?.[0]?.remarks}>
                  {lead?.lead_history?.[0]?.remarks
                    ? lead.lead_history[0].remarks.length > 30
                      ? lead.lead_history[0].remarks.slice(0, 30) + "..."
                      : lead.lead_history[0].remarks
                    : "-"}
                </td> */}
                <Tooltip title={lead?.comments || ""} arrow>
                  <td
                    style={{
                      maxWidth: "150px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      cursor: "pointer",
                    }}
                  >
                    {lead?.comments || "No Comments"}
                  </td>
                </Tooltip>
                <td>
                  {lead?.updated_at
                    ? new Date(
                        lead.updated_at.$date ?? lead.updated_at
                      ).toLocaleString()
                    : ""}
                </td>
                <td>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handleEdit(lead)}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center text-muted">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 my-3">
        <div className="text-muted">
          Showing {start}–{end} of {filteredLeads.length} leads
        </div>

        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select
            value={leadsPerPage}
            onChange={(e) => {
              setLeadsPerPage(Number(e.target.value));
              setCurrentPage(1); // reset page on per-page change
            }}
            style={{ width: "80px" }}
          >
            {[10, 20, 30, 40, 50].map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </Form.Select>
        </div>

        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default LeadTable;
